import { FC, useContext, useEffect, useMemo } from 'react';
import { Avatar, Box, Divider, Group, LoadingOverlay, Stack, Text } from '@mantine/core';
import type { Sx } from '@mantine/core';
import moment from 'moment';
import Moment from 'react-moment';
import { useParams } from 'react-router-dom';
import { useElementSize } from '@mantine/hooks';
import { MessageEventBadge } from '../../Atoms/EventBadge';
import MessagePlaceholderText from '../../Atoms/MessagePlaceholderText';
import { MessageS } from '../../Service/MessageS';
import ResourceAdapter from '../../Service/ResourceAdapter';
import { EmailBoxContext } from '../../Service/Context/EmailBoxContext';
import Attachments from '../../Organismns/Attachments';
import { ThemeText, ThemeTitle } from '../../Atoms/ThemeText';
import { MailS } from '../../Service/MailS';
import ImapAttachment from '../../Organismns/Message/ImapAttachment';
import { useEventScope } from '../../Service/hooks/useEventScope';
import { ProfileContext } from '../../Service/Context/ProfileContext';
import { ProfileShare } from '../../Service/ProfileS';
import MessageActions from './MessageActions';

const truncatedLineSx = {
  maxWidth: '100%',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

const truncatedThemeLineSx: Sx = (theme) => ({
  ...truncatedLineSx,
  color: theme.colorScheme === 'dark' ? 'white' : 'black',
});

const clampedSubjectSx: Sx = (theme) => ({
  maxWidth: '100%',
  minWidth: 0,
  color: theme.colorScheme === 'dark' ? 'white' : 'black',
  '& > *': {
    display: '-webkit-box',
    maxWidth: '100%',
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
});

const plainSubjectSx: Sx = (theme) => ({
  maxWidth: '100%',
  minWidth: 0,
  color: theme.colorScheme === 'dark' ? 'white' : 'black',
  overflow: 'hidden',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
});

const getApplicationUserName = (profileShare?: ProfileShare) =>
  profileShare ? [profileShare.firstName, profileShare.lastName].filter(Boolean).join(' ').trim() : '';

const ChatMessage: FC<{ phoneVersion: boolean }> = ({ phoneVersion }) => {
  const { id: eventId } = useParams();
  const { isEventScope } = useEventScope();
  const { selectedMessage: message, loadingMessage } = useContext(EmailBoxContext);
  const { bookitupTeam } = useContext(ProfileContext);
  const { box } = useParams();
  const { ref } = useElementSize();

  const sentByApplicationUserName = useMemo(() => {
    if (!message?.localDateTime || !message.appUserId) {
      return '';
    }

    const profileShare = bookitupTeam.shares.find((share) => share.applicationUserId === message.appUserId);
    return getApplicationUserName(profileShare);
  }, [bookitupTeam.shares, message?.appUserId, message?.localDateTime]);

  useEffect(() => {
    if (!message?.localDateTime || !message.appUserId) {
      return undefined;
    }

    bookitupTeam.ensureSharesLoaded().catch(() => undefined);
    return undefined;
  }, [bookitupTeam, message?.appUserId, message?.localDateTime]);

  if (!message) {
    return null;
  }

  const customerContact = MessageS.getCustomerContact(message);
  const toEmails = message.to
    ?.map((recipient) => recipient.email)
    .filter(Boolean)
    .join(', ');
  const isHTML = message.message && message.message.startsWith('<');

  return (
    <Box ref={ref} p={0} mt={5}>
      <Group position="apart" noWrap={!phoneVersion} sx={{ alignItems: 'flex-start', width: '100%' }}>
        <Group
          noWrap
          ml={phoneVersion ? 0 : 'sm'}
          spacing="xs"
          sx={{ alignItems: 'flex-start', flex: '1 1 auto', minWidth: 0 }}
        >
          <Avatar radius="xl" size="md" sx={{ flex: '0 0 auto' }}>
            {MailS.getInitials(customerContact)}
          </Avatar>
          <Stack spacing={5} sx={{ flex: '1 1 auto', minWidth: 0, maxWidth: '100%' }}>
            <ThemeTitle order={6} title={customerContact.personDescription} sx={truncatedThemeLineSx}>
              {customerContact.personDescription}
            </ThemeTitle>
            {message.subject && !message.imap && (
              <Box title={message.subject} sx={clampedSubjectSx}>
                <MessagePlaceholderText email={message} value={message.subject} />
              </Box>
            )}
            {message.subject && message.imap && (
              <ThemeText lineClamp={2} title={message.subject} sx={plainSubjectSx}>
                {message.subject}
              </ThemeText>
            )}
            {!message.subject && (
              <Text italic color="dimmed" weight="bolder">
                Kein Betreff
              </Text>
            )}
            {message.from?.email && (
              <ThemeText title={message.from.email} sx={truncatedThemeLineSx}>
                Von:{' '}
                <Text span color="dimmed">
                  {message.from.email}
                </Text>
              </ThemeText>
            )}
            {toEmails && (
              <ThemeText title={toEmails} sx={truncatedThemeLineSx}>
                An:{' '}
                <Text span color="dimmed">
                  {toEmails}
                </Text>
              </ThemeText>
            )}
            {!message.from?.email && !toEmails && (
              <ThemeText title={customerContact.email} sx={truncatedThemeLineSx}>
                {box === 'inbox' ? 'Von: ' : 'An: '}
                <Text span color="dimmed">
                  {customerContact.email}
                </Text>
              </ThemeText>
            )}
            {message.cc && message.cc.length > 0 && (
              <ThemeText mb="xs" title={message.cc.map((c) => c.email).join(', ')} sx={truncatedThemeLineSx}>
                CC:{' '}
                <Text span color="dimmed">
                  {message.cc.map((c) => c.email).join(',')}
                </Text>
              </ThemeText>
            )}
            {message.bcc && message.bcc.length > 0 && (
              <ThemeText mb="xs" title={message.bcc.map((c) => c.email).join(', ')} sx={truncatedThemeLineSx}>
                BCC:{' '}
                <Text span color="dimmed">
                  {message.bcc.map((c) => c.email).join(',')}
                </Text>
              </ThemeText>
            )}
            {!isEventScope && <MessageEventBadge message={message} />}
          </Stack>
        </Group>
        {!phoneVersion && (
          <Box sx={{ flex: '0 0 auto' }}>
            <MessageActions />
          </Box>
        )}
      </Group>

      {!message.imap && (
        <ThemeText size="xs" align="right" mt={phoneVersion ? 'md' : 0}>
          {message.localDateTime && <Moment date={message.localDateTime} calendar />}
          {!message.localDateTime && `Entwurf, ${moment(message.lastModified).calendar()}`}
        </ThemeText>
      )}
      {message.imap && (
        <ThemeText size="xs" align="right">
          {message.localDateTime && <Moment date={message.localDateTime} calendar />}
          {!message.localDateTime && `Entwurf, ${moment(message.lastModified).calendar()}`}
        </ThemeText>
      )}
      {sentByApplicationUserName && (
        <ThemeText size="xs" align="right" color="dimmed">
          Nachricht gesendet von {sentByApplicationUserName}
        </ThemeText>
      )}
      <Divider my="md" />
      <Box pos="relative" sx={{ minHeight: '70vh' }}>
        <LoadingOverlay visible={loadingMessage ?? false} loaderProps={{ size: 'xl' }} />
        {message.imapAttachments &&
          message.imapAttachments.map((f) => (
            <ImapAttachment
              key={f.id ?? f.fileName}
              file={f}
              messageId={message.id ?? message.stringId}
              configId={message.outboundConfigId}
              folderName={message.fullNameFolder}
            />
          ))}
        {!message.imap && message.localDateTime ? (
          <ResourceAdapter
            serviceUrl={`${process.env.REACT_APP_FILE_SERVICE_URL}/files/messages/${message.id}`}
            component={({ data }: any) => {
              data.forEach((file: any) => {
                file.entityType = 'messages';
                file.entityId = message.id;
              });

              return (
                <Attachments
                  attachments={data}
                  eventId={undefined}
                  documentBlueprints={undefined}
                  deleteFileAttachment={undefined}
                  deleteDocumentAttachment={undefined}
                />
              );
            }}
          />
        ) : (
          <Attachments
            attachments={message.attachments}
            eventId={eventId}
            documentBlueprints={undefined}
            deleteFileAttachment={undefined}
            deleteDocumentAttachment={undefined}
          />
        )}
        {!loadingMessage && message.imap && (
          <Box mx={isHTML && phoneVersion ? -15 : 0}>
            <iframe
              title="email-body"
              srcDoc={message.message}
              style={{
                width: '100%',
                height: '90vh',
                border: 'none',
              }}
            />
          </Box>
        )}
        {!message.imap && (
          <ThemeText p="xs">
            <MessagePlaceholderText email={message} value={message.message} />
          </ThemeText>
        )}
      </Box>
    </Box>
  );
};

export default ChatMessage;
